/*
 * Keystrokes: how a shortcut is written down, whether a key press is one, and how it is shown on a
 * `Kbd`. `mod` is the platform's command key, ⌘ on a Mac and Ctrl elsewhere, so one registry entry
 * serves both; which platform this is is read once, when the module loads.
 *
 * Everything here is pure, so the listener in `use-shortcut.ts` is the only part that touches the
 * DOM.
 */

/** One key, as `KeyboardEvent.key` names it, with the modifiers held for it. */
export type Stroke = { key: string; mod?: true; shift?: true; alt?: true }

/** A shortcut's keys: one stroke, or a chord of two pressed one after the other ("G then P"). */
export type Keys = readonly [Stroke] | readonly [Stroke, Stroke]

/** The parts of a key press a shortcut is matched on. A `KeyboardEvent` is one. */
export type Press = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "altKey" | "shiftKey">

/** Whether a platform string names an Apple one, whose command key is ⌘ rather than Ctrl. */
export function isApplePlatform(platform: string) {
  return /mac|iphone|ipad|ipod/i.test(platform)
}

// `userAgentData` is newer than `platform` and not in every browser, nor in TypeScript's DOM types.
const platformName = () => {
  if (typeof navigator === "undefined") return ""
  const { userAgentData } = navigator as { userAgentData?: { platform?: string } }
  return userAgentData?.platform || navigator.platform || navigator.userAgent
}

/** Whether this browser runs on an Apple platform: read once, as the page loads. */
export const apple = isApplePlatform(platformName())

const letter = /^[a-z]$/i

/**
 * Whether a key press is a stroke. A letter matches either case, with Shift only when the stroke
 * asks for it, so ⇧C is not C. A symbol is matched on the character typed, since the Shift a
 * layout needs to type `?` is part of the character, not a modifier the stroke asks for.
 */
export function strokeMatches(stroke: Stroke, press: Press, onApple = apple) {
  const mod = onApple ? press.metaKey : press.ctrlKey
  const otherMod = onApple ? press.ctrlKey : press.metaKey
  if (Boolean(stroke.mod) !== mod || otherMod || Boolean(stroke.alt) !== press.altKey) return false
  if (letter.test(stroke.key))
    return (
      press.key.toLowerCase() === stroke.key.toLowerCase() &&
      Boolean(stroke.shift) === press.shiftKey
    )
  // A named key — Enter, ArrowUp — takes Shift as a modifier; a typed symbol does not.
  if (stroke.key.length > 1 && Boolean(stroke.shift) !== press.shiftKey) return false
  if (stroke.shift && !press.shiftKey) return false
  return press.key === stroke.key
}

const modifierKeys = new Set(["Shift", "Control", "Alt", "Meta", "AltGraph", "CapsLock", "Fn"])

/** Whether a press is a modifier going down on its own, on the way to the stroke it modifies. */
export function isModifierOnly(press: Pick<Press, "key">) {
  return modifierKeys.has(press.key)
}

const keyNames: Record<string, string> = {
  Enter: "↵",
  Escape: "Esc",
  ArrowUp: "↑",
  ArrowDown: "↓",
  ArrowLeft: "←",
  ArrowRight: "→",
  " ": "Space",
}

/** A stroke's chips on a `Kbd`, modifiers first: ⌘ K, or Ctrl K off a Mac. */
export function chipsOf(stroke: Stroke, onApple = apple): string[] {
  return [
    ...(stroke.mod ? [onApple ? "⌘" : "Ctrl"] : []),
    ...(stroke.alt ? [onApple ? "⌥" : "Alt"] : []),
    ...(stroke.shift ? ["⇧"] : []),
    keyNames[stroke.key] ?? stroke.key.toUpperCase(),
  ]
}

/** A shortcut's chips, a list per stroke: one list for ⌘K, two for a chord, with "then" between. */
export function keysOf(keys: Keys, onApple = apple): string[][] {
  return keys.map((stroke) => chipsOf(stroke, onApple))
}

/**
 * A shortcut as text, for a tooltip or a label that can't hold a `Kbd`: ⌘K on a Mac and Ctrl+K
 * elsewhere, as each platform writes its own, and a chord as "G then P".
 */
export function describeKeys(keys: Keys, onApple = apple) {
  return keysOf(keys, onApple)
    .map((chips) => chips.join(onApple ? "" : "+"))
    .join(" then ")
}
